<template>
  <div>
    <AddressAutocompleteKleber
      label="Address"
      :show-icon="false"
      placeholder="Address"
      :manualInput="manualInputString"
      @onChange="onAddressChange"
    />
    <div class="text-sm">
      <span class="font-italic">Can't find an address?</span>&nbsp;
      <span class="text-blue-600 underline cursor-pointer" @click="() => (showManualInput = true)"
        >Enter it manually</span
      >
    </div>
    <label v-if="errorMessages" style="color: red; font-size: 14px" class="text-red required bold-400">{{
      errorMessages
    }}</label>
    <div class="modal-container box-shadow" style="z-index: 20 !important">
      <Dialog
        class="dialog-wrapper"
        :visible="showManualInput"
        :style="{ width: '50vw', 'z-index': '20 !important' }"
        :breakpoints="{ '1199px': '75vw', '575px': '90vw' }"
        :modal="true"
      >
        <div class="manual-address">
          <div>
            <div class="col col-12 manual-field">
              <div class="font-bold">Address line 1</div>
              <TextField
                class="p-0"
                width="100%"
                placeholder="Enter address line 1"
                :value="manualInput.line1"
                @onInput="getFormData($event, 'line1')"
                :errorMessages="showErrors('line1')"
              />
            </div>
            <div class="col col-12 manual-field">
              <div class="font-bold">Address line 2</div>
              <TextField
                width="100%"
                placeholder="Enter address line 2"
                :value="manualInput.line2"
                @onInput="getFormData($event, 'line2')"
                :errorMessages="showErrors('line2')"
              />
            </div>
            <div class="col md:col-8 col-12 manual-field">
              <div class="font-bold">Suburb</div>
              <TextField
                width="100%"
                placeholder="Enter suburb"
                :value="manualInput.city"
                @onInput="getFormData($event, 'city')"
                :errorMessages="showErrors('city')"
              />
            </div>
            <div class="flex col md:col-8 col-12">
              <div class="col col-6 p-0 pr-2 manual-field">
                <div class="font-bold">State</div>
                <Dropdown
                  :value="manualInput.state?.toUpperCase()"
                  :items="statesList"
                  placeholder="State"
                  width="100%"
                  @change="getFormData($event, 'state')"
                  :errorMessages="showErrors('state')"
                />
              </div>
              <div class="col col-6 p-0 pl-2 manual-field">
                <div class="font-bold">Postal code</div>
                <TextField
                  @onInput="getFormData($event, 'postCode')"
                  :value="manualInput.postCode"
                  width="100%"
                  placeholder="Enter postal code"
                  type="tel"
                  pattern="[0-9]*"
                  :maxLength="POST_CODE_LENGTH"
                  :errorMessages="showErrors('postCode') || postCodeError"
                />
              </div>
            </div>
          </div>
        </div>
        <template #footer>
          <div class="flex col justify-content-start align-items-center gap-4">
            <Button :loading="isLoading" label="Use address" type="primary" @click="confirmManualAddress()" />
            <span class="underline cursor-pointer" @click="closeModal()">Cancel</span>
          </div>
        </template>
      </Dialog>
    </div>
  </div>
</template>

<script>
import { POST_CODE_LENGTH, POST_CODE_PATTERN, STATES } from '@/constants';
import { TECHLEND_BE_URL } from '@/constants.api';
import { _get } from '@/services/crudService';
import { helpers, required } from '@vuelidate/validators';
import { useVuelidate } from '@vuelidate/core';
import AddressAutocompleteKleber from '@/components/common/AddressAutocompleteKleber.vue';
import Dialog from 'primevue/dialog';

export default {
  components: { AddressAutocompleteKleber, Dialog },
  setup: () => ({ v$: useVuelidate() }),
  props: {
    errorMessages: { type: String, default: '' },
    value: { type: Object, default: () => ({}) },
  },
  data() {
    const address = {
      line1: this.value?.line1 || '',
      line2: this.value?.line2 || '',
      city: this.value?.city || '',
      country: this.value?.country || '',
      postCode: this.value?.postCode || '',
      state: this.value?.state || '',
    };
    return {
      POST_CODE_LENGTH,
      showManualInput: false,
      statesList: STATES,
      isLoading: false,
      address,
      manualInput: { ...address, country: address.country || 'au' },
      // Seeds the visible autocomplete text so a previously-chosen address survives this
      // component being unmounted/remounted (e.g. navigating between the multi-step dialog's steps).
      manualInputString: address.line1
        ? `${address.line1} ${address.line2}, ${address.city}, ${address.state} ${address.postCode}`
        : '',
      postCodeError: '',
    };
  },
  validations() {
    return {
      manualInput: {
        line1: { required: helpers.withMessage('Address line 1 is required', required) },
        city: { required: helpers.withMessage('Suburb is required', required) },
        state: { required: helpers.withMessage('State is required', required) },
        postCode: {
          required: helpers.withMessage('Post code is required', required),
          format: helpers.withMessage(`Post code must be ${POST_CODE_LENGTH} digits`, helpers.regex(POST_CODE_PATTERN)),
        },
      },
    };
  },
  methods: {
    getFormData(value, fieldName) {
      if (fieldName === 'postCode') {
        this.postCodeError = '';
      }
      this.manualInput[fieldName] = value;
      this.manualInput = { ...this.manualInput };
    },
    async onAddressChange(address) {
      this.address.line1 = address.address_1 ? address.address_1 : '';
      this.address.line2 = address.address_2 ? address.address_2 : '';
      this.address.city = address.address_1 ? address.city : '';
      this.address.country = address.country ? address.country : '';
      this.address.postCode = address.postCode ? String(address.postCode) : '';
      this.address.state = address.state ? address.state : '';
      this.$emit('onAddressChange', {
        line1: this.address.line1,
        line2: this.address.line2,
        city: this.address.city,
        postCode: this.address.postCode,
        state: this.address.state,
      });
      this.manualInput = this.address;
      this.v$.$reset();
    },
    showErrors(fieldName) {
      return (this.v$.manualInput[fieldName]?.$errors ?? []).map((e) => e.$message).join('. ');
    },
    closeModal() {
      this.v$.$reset();
      this.showManualInput = false;
    },
    async confirmManualAddress() {
      const isFormValid = await this.v$.$validate();
      if (!isFormValid) return;
      this.isLoading = true;
      try {
        const response = await _get(`${TECHLEND_BE_URL}/public/post-code?postCode=${this.manualInput.postCode}`);
        if (response.data.postCode.state !== this.manualInput.state.toLowerCase()) {
          this.postCodeError = 'Post code is not in state';
          return;
        }
      } catch (error) {
        this.postCodeError = 'Invalid post code';
        return;
      } finally {
        this.isLoading = false;
      }
      this.$emit('onAddressChange', {
        line1: this.manualInput.line1,
        line2: this.manualInput.line2,
        city: this.manualInput.city,
        postCode: this.manualInput.postCode,
        state: this.manualInput.state,
      });
      this.manualInputString = `${this.manualInput.line1} ${this.manualInput.line2}, ${this.manualInput.city}, ${this.manualInput.state} ${this.manualInput.postCode}`;
      this.closeModal();
    },
  },
};
</script>

<style lang="scss" scoped>
// Each field here is a bold caption followed by an input component, and every one of those
// components renders an empty `.p-float-label` above its control. Nothing in this file used to set
// the space between caption and control: it came from an unscoped `.p-float-label` margin declared
// in an unrelated component, which leaks app-wide. So the spacing of this form was decided by
// whichever unrelated component happened to be loaded, and State only lined up with Postal code
// while that margin and any margin inside the input component collapsed into a single gap.
//
// The fix is to leave exactly one margin in the chain. Zeroing the float labels drops the leaked
// rule, and spacing the caption directly puts the one remaining margin on an element this file
// owns. With a single source per column there is nothing left to collapse, double, or lose, so the
// two controls keep a shared baseline whatever is rendered around them. 1rem reproduces the
// spacing these fields already had, so nothing moves.
// Deliberately not called `field`: PrimeFlex owns that class and gives it its own bottom margin
// and a `> label { display: block }` rule, both of which would change this modal's layout.
.manual-address {
  .manual-field > .font-bold {
    margin-bottom: 1rem;
  }

  .manual-field :deep(.p-float-label) {
    margin: 0;
  }
}
</style>
